import { animate, keyframes, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { Style } from '@local/client-contracts';
import { extractInitials } from '@local/ts-infra';
import { TelemetryService } from '@shared/services';
import { ApplicationsService } from '@shared/services/applications.service';
import { LinksService } from '@shared/services/links.service';
import { EVENT_LABELS, SyncingStateData, SyncingStateDisplayData } from './models';
import { config } from './syncing-state.config';

@Component({
  selector: 'syncing-state',
  templateUrl: './syncing-state.component.html',
  styleUrls: ['./syncing-state.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('pulse', [
      transition(
        ':enter',
        animate(1000, keyframes([style({ opacity: 0 }), style({ opacity: 1 }), style({ opacity: 0 }), style({ opacity: 1 })]))
      ),
    ]),
    trigger('fade', [
      state('in', style({ opacity: 1 })),
      transition(':enter', [style({ opacity: 0 }), animate(200)]),
      transition(':leave', animate(250, style({ opacity: 0 }))),
    ]),
  ],
})
export class SyncingStateComponent implements OnInit {
  @Input() data: SyncingStateData;
  displayData: SyncingStateDisplayData;

  constructor(
    private cdr: ChangeDetectorRef,
    private applicationsService: ApplicationsService,
    private linksService: LinksService,
    private telemetryService: TelemetryService
  ) {}

  async ngOnInit() {
    this.displayData = await this.initByStatus();
    this.sendShowEvent();
    this.cdr.markForCheck();
  }

  private sendShowEvent(): void {
    const label: string = EVENT_LABELS[this.data.status];
    this.telemetryService.event({
      category: 'status_view',
      name: 'show',
      label,
      location: {
        title: this.data.location,
      },
    });
  }

  async initByStatus(): Promise<SyncingStateDisplayData> {
    let { status, appId, linkId } = this.data;
    if (!appId && !linkId) return;

    if (!linkId && appId) {
      const app = await this.applicationsService.one(appId);
      return {
        ...config[status],
        icon: app?.icon,
      };
    }

    const link = await this.linksService.one(linkId);
    if (!appId) appId = link.appId;

    const app = await this.applicationsService.one(appId);

    const accountIcon: Style.EntityIcon<Style.EntityIconType> = {
      type: 'account',
      value: { type: 'initials', value: { initials: extractInitials(link.name), color: link.color } },
    };
    const account = {
      icon: accountIcon,
      name: link.name,
    };
    return {
      ...config[status],
      icon: app?.icon,
      account,
    };
  }
}
